import { createSlice } from "@reduxjs/toolkit";
import {
  getDashboardAdsLink,
  getKPIs,
  getKPIMap,
  setKPIIndex,
  getCounter,
  getFutureMonetaryValue,
  getCancelStat,
  getLastActivities,
  getProjectUnactivity,
  getSalesStatistic,
  getSalesCounter,
  getPhoneCallSourceStat,
} from "../thunks/dashboard.Thunks";
import {
  AdsLink,
  CancelStat,
  FutureMonetaryValue,
  KPIDTO,
  KPIMapDTO,
  LastActivity,
  ProjectUnactivity,
  SalesCounterJson,
  SalesStatistic,
  PhoneCallSourceStat,
} from "../../models/Dashboard.model";

interface DashboardState {
  kpiMapLoading: boolean;
  kpiMapError: string | null;
  kpiMapSuccess: boolean;
  KpiMaps: KPIMapDTO[] | null;
  kpisLoading: boolean;
  kpisError: string | null;
  kpisSuccess: boolean;
  kpis: KPIDTO[] | null;
  adsLinkLoading: boolean;
  adsLinkError: string | null;
  adsLinkSuccess: boolean;
  adsLinks: AdsLink | null;
  setKPIIndexLoading: boolean;
  setKPIIndexError: string | null;
  setKPIIndexSuccess: boolean;
  getCounterLoading: boolean;
  getCounterError: string | null;
  getCounterSuccess: boolean;
  contractsCounter: SalesCounterJson | null;
  menusCounter: SalesCounterJson | null;
  leadsCounter: SalesCounterJson | null;
  appointmentCounter: SalesCounterJson | null;
  FutureMonetaryValueLoading: boolean;
  FutureMonetaryValueError: string | null;
  FutureMonetaryValueSuccess: boolean;
  futureMonetaryValue: FutureMonetaryValue | null;
  cancelStatLoading: boolean;
  cancelStatError: string | null;
  cancelStatSuccess: boolean;
  cancelStat: CancelStat[] | null;
  lastActivitiesLoading: boolean;
  lastActivitiesError: string | null;
  lastActivitiesSuccess: boolean;
  lastActivities: LastActivity[] | null;
  projectUnactivityLoading: boolean;
  projectUnactivityError: string | null;
  projectUnactivitySuccess: boolean;
  projectUnactivity: ProjectUnactivity[] | null;
  salesStatisticLoading: boolean;
  salesStatisticError: string | null;
  salesStatisticSuccess: boolean;
  salesStatistics: SalesStatistic[] | null;
  salesCounterLoading: boolean;
  salesCounterError: string | null;
  salesCounterSuccess: boolean;
  salesCounterLeadsJson: SalesCounterJson | null;
  salesCounterDealsJson: SalesCounterJson | null;
  salesCounterLostsJson: SalesCounterJson | null;
  salesCounterMeetingsJson: SalesCounterJson | null;
  salesCounterDealPercentJson: SalesCounterJson | null;
  phoneCallSourceStatsLoading: boolean;
  phoneCallSourceStatsError: string | null;
  phoneCallSourceStatsSuccess: boolean;
  phoneCallSourceStats: PhoneCallSourceStat[] | null;
  selectedMonth: number;
  selectedYear: number;
}

const initialState: DashboardState = {
  kpiMapLoading: false,
  kpiMapError: null,
  kpiMapSuccess: false,
  KpiMaps: null,
  kpisLoading: false,
  kpisError: null,
  kpisSuccess: false,
  kpis: null,
  adsLinkLoading: false,
  adsLinkError: null,
  adsLinkSuccess: false,
  adsLinks: null,
  setKPIIndexLoading: false,
  setKPIIndexError: null,
  setKPIIndexSuccess: false,
  getCounterLoading: false,
  getCounterError: null,
  getCounterSuccess: false,
  contractsCounter: null,
  menusCounter: null,
  leadsCounter: null,
  appointmentCounter: null,
  FutureMonetaryValueLoading: false,
  FutureMonetaryValueError: null,
  FutureMonetaryValueSuccess: false,
  futureMonetaryValue: null,
  cancelStatLoading: false,
  cancelStatError: null,
  cancelStatSuccess: false,
  cancelStat: null,
  lastActivitiesLoading: false,
  lastActivitiesError: null,
  lastActivitiesSuccess: false,
  lastActivities: null,
  projectUnactivityLoading: false,
  projectUnactivityError: null,
  projectUnactivitySuccess: false,
  projectUnactivity: null,
  salesStatisticLoading: false,
  salesStatisticError: null,
  salesStatisticSuccess: false,
  salesStatistics: null,
  salesCounterLoading: false,
  salesCounterError: null,
  salesCounterSuccess: false,
  salesCounterLeadsJson: null,
  salesCounterDealsJson: null,
  salesCounterLostsJson: null,
  salesCounterMeetingsJson: null,
  salesCounterDealPercentJson: null,
  phoneCallSourceStatsLoading: false,
  phoneCallSourceStatsError: null,
  phoneCallSourceStatsSuccess: false,
  phoneCallSourceStats: null,
  selectedMonth: new Date().getMonth() + 1,
  selectedYear: new Date().getFullYear(),
};

const dashboardSlice = createSlice({
  name: "dashboard",
  initialState,
  reducers: {
    clearDashboardMessages: (state) => {
      state.kpiMapError = null;
      state.kpiMapSuccess = false;
      state.KpiMaps = null;
      state.kpisError = null;
      state.kpisSuccess = false;
      state.kpis = null;
      state.adsLinkError = null;
      state.adsLinkSuccess = false;
      state.adsLinks = null;
      state.FutureMonetaryValueError = null;
      state.FutureMonetaryValueSuccess = false;
      state.futureMonetaryValue = null;
      state.cancelStatError = null;
      state.cancelStatSuccess = false;
      state.cancelStat = null;
      state.lastActivitiesError = null;
      state.lastActivitiesSuccess = false;
      state.lastActivities = null;
      state.projectUnactivityError = null;
      state.projectUnactivitySuccess = false;
      state.projectUnactivity = null;
      state.phoneCallSourceStatsError = null;
      state.phoneCallSourceStatsSuccess = false;
      state.phoneCallSourceStats = null;
    },
    setDashboardDate: (
      state,
      action: { payload: { month: number; year: number } },
    ) => {
      state.selectedMonth = action.payload.month;
      state.selectedYear = action.payload.year;
    },
    updateKpiMapItem: (
      state,
      action: { payload: { locationIndex: number; kpiId: number } },
    ) => {
      if (!state.KpiMaps) return;
      const item = state.KpiMaps.find(
        (m) => m.location_Index === action.payload.locationIndex,
      );
      if (item) {
        item.kpI_ID = action.payload.kpiId;
        item.kPI_ID = action.payload.kpiId;
      } else {
        state.KpiMaps.push({
          location_Index: action.payload.locationIndex,
          kpI_ID: action.payload.kpiId,
          kPI_ID: action.payload.kpiId,
        });
      }
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getKPIMap.pending, (state) => {
        state.kpiMapLoading = true;
        state.kpiMapError = null;
        state.kpiMapSuccess = false;
      })
      .addCase(getKPIMap.fulfilled, (state, action) => {
        state.kpiMapLoading = false;
        state.kpiMapSuccess = true;
        state.KpiMaps = action.payload.kpiMap || null;
      })
      .addCase(getKPIMap.rejected, (state, action) => {
        state.kpiMapLoading = false;
        state.kpiMapError = action.payload || "Failed to get KPI map";
        state.kpiMapSuccess = false;
      })
      .addCase(getKPIs.pending, (state) => {
        state.kpisLoading = true;
        state.kpisError = null;
        state.kpisSuccess = false;
      })
      .addCase(getKPIs.fulfilled, (state, action) => {
        state.kpisLoading = false;
        state.kpisSuccess = true;
        state.kpis = action.payload.kpIs || null;
      })
      .addCase(getKPIs.rejected, (state, action) => {
        state.kpisLoading = false;
        state.kpisError = action.payload || "Failed to get KPIs";
        state.kpisSuccess = false;
      })
      .addCase(getDashboardAdsLink.pending, (state) => {
        state.adsLinkLoading = true;
        state.adsLinkError = null;
        state.adsLinkSuccess = false;
      })
      .addCase(getDashboardAdsLink.fulfilled, (state, action) => {
        state.adsLinkLoading = false;
        state.adsLinkSuccess = true;
        state.adsLinks = action.payload.adsLinks || null;
      })
      .addCase(getDashboardAdsLink.rejected, (state, action) => {
        state.adsLinkLoading = false;
        state.adsLinkError =
          action.payload || "Failed to get dashboard ads link";
        state.adsLinkSuccess = false;
      })
      .addCase(setKPIIndex.pending, (state) => {
        state.setKPIIndexLoading = true;
        state.setKPIIndexError = null;
        state.setKPIIndexSuccess = false;
      })
      .addCase(setKPIIndex.fulfilled, (state) => {
        state.setKPIIndexLoading = false;
        state.setKPIIndexSuccess = true;
      })
      .addCase(setKPIIndex.rejected, (state, action) => {
        state.setKPIIndexLoading = false;
        state.setKPIIndexError = action.payload || "Failed to set KPI index";
        state.setKPIIndexSuccess = false;
      })
      .addCase(getCounter.pending, (state) => {
        state.getCounterLoading = true;
        state.getCounterError = null;
        state.getCounterSuccess = false;
      })
      .addCase(getCounter.fulfilled, (state, action) => {
        state.getCounterLoading = false;
        state.getCounterSuccess = true;
        const key = action.meta.arg.counterKey;
        if (key === "CONTRACTS") {
          state.contractsCounter = action.payload.counterJson;
        } else if (key === "MENUS") {
          state.menusCounter = action.payload.counterJson;
        } else if (key === "LEADS") {
          state.leadsCounter = action.payload.counterJson;
        } else if (key === "APPOINTMENTS") {
          state.appointmentCounter = action.payload.counterJson;
        }
      })
      .addCase(getCounter.rejected, (state, action) => {
        state.getCounterLoading = false;
        state.getCounterError = action.payload || "Failed to get counter";
        state.getCounterSuccess = false;
      })
      .addCase(getFutureMonetaryValue.pending, (state) => {
        state.FutureMonetaryValueLoading = true;
        state.FutureMonetaryValueError = null;
        state.FutureMonetaryValueSuccess = false;
      })
      .addCase(getFutureMonetaryValue.fulfilled, (state, action) => {
        state.FutureMonetaryValueLoading = false;
        state.FutureMonetaryValueSuccess = true;
        state.futureMonetaryValue = action.payload.futureMonetaryValue || null;
      })
      .addCase(getFutureMonetaryValue.rejected, (state, action) => {
        state.FutureMonetaryValueLoading = false;
        state.FutureMonetaryValueError =
          action.payload || "Failed to get future monetary value";
        state.FutureMonetaryValueSuccess = false;
      })
      .addCase(getCancelStat.pending, (state) => {
        state.cancelStatLoading = true;
        state.cancelStatError = null;
        state.cancelStatSuccess = false;
      })
      .addCase(getCancelStat.fulfilled, (state, action) => {
        state.cancelStatLoading = false;
        state.cancelStatSuccess = true;
        state.cancelStat = action.payload.cancelStat || null;
      })
      .addCase(getCancelStat.rejected, (state, action) => {
        state.cancelStatLoading = false;
        state.cancelStatError = action.payload || "Failed to get cancel stat";
        state.cancelStatSuccess = false;
      })
      .addCase(getLastActivities.pending, (state) => {
        state.lastActivitiesLoading = true;
        state.lastActivitiesError = null;
        state.lastActivitiesSuccess = false;
      })
      .addCase(getLastActivities.fulfilled, (state, action) => {
        state.lastActivitiesLoading = false;
        state.lastActivitiesSuccess = true;
        state.lastActivities = action.payload.lastActivities || null;
      })
      .addCase(getLastActivities.rejected, (state, action) => {
        state.lastActivitiesLoading = false;
        state.lastActivitiesError =
          action.payload || "Failed to get last activities";
        state.lastActivitiesSuccess = false;
      })
      .addCase(getProjectUnactivity.pending, (state) => {
        state.projectUnactivityLoading = true;
        state.projectUnactivityError = null;
        state.projectUnactivitySuccess = false;
      })
      .addCase(getProjectUnactivity.fulfilled, (state, action) => {
        state.projectUnactivityLoading = false;
        state.projectUnactivitySuccess = true;
        state.projectUnactivity = action.payload.projectUnactivity || null;
      })
      .addCase(getProjectUnactivity.rejected, (state, action) => {
        state.projectUnactivityLoading = false;
        state.projectUnactivityError =
          action.payload || "Failed to get project unactivity";
        state.projectUnactivitySuccess = false;
      })
      .addCase(getSalesStatistic.pending, (state) => {
        state.salesStatisticLoading = true;
        state.salesStatisticError = null;
        state.salesStatisticSuccess = false;
      })
      .addCase(getSalesStatistic.fulfilled, (state, action) => {
        state.salesStatisticLoading = false;
        state.salesStatisticSuccess = true;
        state.salesStatistics = action.payload.salesStatistics || null;
      })
      .addCase(getSalesStatistic.rejected, (state, action) => {
        state.salesStatisticLoading = false;
        state.salesStatisticError =
          action.payload || "Failed to get sales statistics";
        state.salesStatisticSuccess = false;
      })
      .addCase(getSalesCounter.pending, (state) => {
        state.salesCounterLoading = true;
        state.salesCounterError = null;
        state.salesCounterSuccess = false;
      })
      .addCase(getSalesCounter.fulfilled, (state, action) => {
        state.salesCounterLoading = false;
        state.salesCounterSuccess = true;
        const key = action.meta.arg.salesCounterKey;
        const json = action.payload.salesCounterJson || null;
        if (key === "LEADS") state.salesCounterLeadsJson = json;
        else if (key === "DEALS") state.salesCounterDealsJson = json;
        else if (key === "LOSTS") state.salesCounterLostsJson = json;
        else if (key === "MEETINGS") state.salesCounterMeetingsJson = json;
        else if (key === "DEALPERCENT")
          state.salesCounterDealPercentJson = json;
      })
      .addCase(getSalesCounter.rejected, (state, action) => {
        state.salesCounterLoading = false;
        state.salesCounterError =
          action.payload || "Failed to get sales counter";
        state.salesCounterSuccess = false;
      })
      .addCase(getPhoneCallSourceStat.pending, (state) => {
        state.phoneCallSourceStatsLoading = true;
        state.phoneCallSourceStatsError = null;
        state.phoneCallSourceStatsSuccess = false;
      })
      .addCase(getPhoneCallSourceStat.fulfilled, (state, action) => {
        state.phoneCallSourceStatsLoading = false;
        state.phoneCallSourceStatsSuccess = true;
        state.phoneCallSourceStats =
          action.payload.phoneCallSourceStats || null;
      })
      .addCase(getPhoneCallSourceStat.rejected, (state, action) => {
        state.phoneCallSourceStatsLoading = false;
        state.phoneCallSourceStatsError =
          action.payload || "Failed to get phone call source stats";
        state.phoneCallSourceStatsSuccess = false;
      });
  },
});

export const { clearDashboardMessages, updateKpiMapItem, setDashboardDate } =
  dashboardSlice.actions;
export default dashboardSlice.reducer;
